<template>
    <div
        v-if="showBaselayerToggler"
        :title="$t('switchBaseLayer')"
        class="baselayers"
    >
        <template v-if="mode === 'select'">
            <b-row
                v-for="baseLayer in baseLayers"
                :key="baseLayer.extended.layerName"
                class="mb-2"
            >
                <b-col v-if="activeBaseLayer === baseLayer.id">
                    <img
                        :src="baseLayer.extended.baselayerImage"
                        :alt="baseLayer.extended.layerName"
                    />
                    <label class="ml-2"
                        >{{ baseLayer.extended.title }} ({{
                            $t('selected')
                        }})</label
                    >
                </b-col>
                <b-col v-else>
                    <a
                        @click="$_selectBaselayer(baseLayer.id)"
                        href="#"
                        class="accessible-link"
                    >
                        <img
                            :src="baseLayer.extended.baselayerImage"
                            :alt="baseLayer.extended.layerName"
                        />
                        <label class="ml-2">{{
                            baseLayer.extended.title
                        }}</label>
                    </a>
                </b-col>
            </b-row>
        </template>
        <template v-else>
            <a id="map_controls_layers" @click="$_toggleBaselayers">
                <img
                    :src="baselayerIcon"
                    :alt="$t('baseLayerToggleImageAlt')"
                />
            </a>
        </template>
    </div>
</template>

<script>
    import { mapState, mapGetters } from 'vuex';
    import { isInRange } from '../../../services/map';
    import {
        isInFilteredList,
        getNextId
    } from '../../../services/utilities/baseLayer';
    export default {
        props: { mode: { type: String, required: false } },
        data() {
            return {
                baselayerIcon:
                    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAYAAACqaXHeAAAAAXNSR0IArs' +
                    '4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAAYdEVYdFNvZnR3YXJlAHBhaW50Lm5ldCA0Lj' +
                    'AuNvyMY98AAACSSURBVHhe5cgxAQAADMOg+TfdGYgDDh5uGy1TkinJlGRKMiWZkkxJpiRTkinJlGRKMiWZkkxJpiRTki' +
                    'nJlGRKMiWZkkxJpiRTkinJlGRKMiWZkkxJpiRTkinJlGRKMiWZkkxJpiRTkinJlGRKMiWZkkxJpiRTkinJlGRKMiWZkkx' +
                    'JpiRTkinJlGRKMiWZkkzH7gEulNLCLyAl/QAAAABJRU5ErkJggg==',
                selectedBaselayerIndex: 0,
                previousResolution: 0
            };
        },

        mounted() {
            this.previousResolution = this.resolution;
        },

        computed: {
            ...mapState(['defaultMap']),
            ...mapGetters(['getBaseLayers', 'getLayerById', 'getUserData']),
            baseLayers: function () {
                return this.getBaseLayers;
            },

            resolution() {
                return (
                    Math.round(
                        this.$store.getters.getMainMap?.viewport?.resolution *
                            100000
                    ) / 100000
                );
            },

            filteredBaseLayers() {
                return this.baseLayers.filter((b) =>
                    isInRange(
                        this.resolution,
                        b.data.minBaseLayerResolution,
                        b.data.maxBaseLayerResolution
                    )
                );
            },
            activeBaseLayer() {
                return this.defaultMap?.baseLayer?.id;
            },

            nextBaseLayer: function () {
                if (this.$_loading()) {
                    return undefined;
                }

                // When no filtered base layers available remove current base layer.
                if (!this.filteredBaseLayers?.length) {
                    if (this.activeBaseLayer) {
                        // remove active layer
                        this.$store.commit('REMOVE_BASE_LAYER_FROM_MAP', {
                            mapName: null
                        });
                    }

                    return undefined;
                }

                // Is the current layer still in range of the present resolution?
                const inRange = isInFilteredList(
                    this.activeBaseLayer,
                    this.filteredBaseLayers
                );

                const zoomDirection = this.$_zoomDirection();

                // when loading the page the activeBaseLayer may be empty
                // or may be replaced by a layer not in range.
                // get the first from filteredList
                var nextId;

                if (!this.activeBaseLayer || (!inRange && !zoomDirection)) {
                    nextId = this.filteredBaseLayers[0].id;
                } else {
                    // Get the next layer in order of the base layers.
                    nextId = getNextId(
                        this.baseLayers,
                        this.filteredBaseLayers,
                        this.activeBaseLayer,
                        zoomDirection ?? 'in' // Default forward search
                    );
                }

                // Cuurent layer is not in range anymore. Set the next layer as current
                if (!inRange || !this.activeBaseLayer) {
                    this.$_selectBaselayer(nextId);
                }

                return this.getLayerById(nextId);
            },
            /*
             * The BaselayerToggler will not be shown for single baselayer
             */
            showBaselayerToggler() {
                return this.filteredBaseLayers?.length > 1;
            }
        },

        methods: {
            $_loading() {
                var loading = true;
                // wait till all layers have been loaded.
                // loading has finished when userdata baselayers equals baselayers
                if (this.getUserData && this.getUserData.baselayers) {
                    loading =
                        this.getUserData.baselayers.length !=
                        this.baseLayers.length;
                }

                return loading;
            },

            /*
             * Toggles the baselayers on the map
             */
            $_toggleBaselayers() {
                this.$store.dispatch('addLayerToMap', {
                    mapName: null,
                    id: this.nextBaseLayer.id
                });
            },
            $_selectBaselayer(id) {
                this.$store.dispatch('addLayerToMap', { mapName: null, id });
            },

            // Determine whether we are zooming in or out.
            $_zoomDirection() {
                let direction = null;
                if (this.previousResolution < this.resolution) {
                    direction = 'out';
                } else if (this.previousResolution > this.resolution) {
                    direction = 'in';
                }
                // save last known resolution
                this.previousResolution = this.resolution;

                return direction;
            }
        },

        watch: {
            nextBaseLayer(layer) {
                if (layer?.extended) {
                    this.baselayerIcon = layer.extended?.baselayerImage;
                }
            }
        }
    };
</script>

<style scoped>
.baselayers {
    border: 1px solid #fff;
    margin-right: 5px;
}
#map_controls_layers {
    cursor: pointer;
    border: 2px #007ac9 solid;
    width: 60px;
    height: 60px;
    display: inline-block;
    background-color: #ffffff;
    box-sizing: content-box;
}
#map_controls_layers img {
    width: 60px;
    height: 60px;
}
/*-------------------------------------
max-width : 767px
---------------------------------------*/
@media (max-width: 767px) {
    #map_controls_layers {
        width: 45px;
        height: 45px;
    }
    #map_controls_layers img {
        width: 45px;
        height: 45px;
    }
}
</style>
